import * as React from 'react';
import clsx from 'clsx';
import { getFullImageUrl } from '@/utils/url';
import { AvatarWaves } from './AvatarWaves';

export type VideoPlaybackMode = 'loop' | 'single' | 'boomerang';

/** Props for the {@link EchoAvatar} component. */
export interface EchoAvatarProps {
  /** Image URL for the avatar. Falls back to initials when absent or broken. */
  src?: string | null;
  videoSrc?: string | null;
  /** Alt text for the image. Also used for fallback initials. */
  alt?: string;
  /** Explicit initials override (defaults to first two chars of `alt`). */
  initials?: string;
  /** Additional CSS classes for the outer wrapper. */
  className?: string;
  /** Render decorative waves on the left side as well as the right. */
  enableLeftWaves?: boolean;
  /** Avatar diameter in pixels, scaled from a 51.37 px base circle. */
  size?: number;
  videoPlaybackMode?: VideoPlaybackMode;
  /** Render as a small centered icon instead of a full-bleed photo. */
  iconMode?: boolean;
}

/**
 * Circular avatar with decorative wave arcs and image/initials fallback.
 * Scales proportionally from a 51.37 px base size using CSS transforms.
 */
export const EchoAvatar: React.FC<EchoAvatarProps> = ({
  src,
  videoSrc,
  alt = 'Echo',
  initials,
  className,
  enableLeftWaves = false,
  size = 52,
  videoPlaybackMode = 'loop',
  iconMode = false,
}) => {
  const [imgError, setImgError] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    setImgError(false);
  }, [src]);

  // Boomerang playback: play forward → reverse frame-by-frame → repeat
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc || videoPlaybackMode !== 'boomerang') return;

    let animId: number;
    const STEP = 1 / 30; // ~30fps reverse playback

    const onEnded = () => {
      video.pause();
      const reverseStep = () => {
        if (video.currentTime <= 0.05) {
          video.currentTime = 0;
          video.play();
          return;
        }
        video.currentTime = Math.max(0, video.currentTime - STEP);
        animId = requestAnimationFrame(reverseStep);
      };
      animId = requestAnimationFrame(reverseStep);
    };

    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('ended', onEnded);
      cancelAnimationFrame(animId);
    };
  }, [videoPlaybackMode, videoSrc]);

  const BASE_CIRCLE_SIZE = 51.37;
  const scale = size / BASE_CIRCLE_SIZE;
  const baseContainerWidth = enableLeftWaves ? 96 : 74;
  const displayWidth = baseContainerWidth * scale;
  const displayHeight = size;

  const finalSrc = React.useMemo(() => getFullImageUrl(src), [src]);
  const showImage = finalSrc && !imgError;

  return (
    <div
      className={clsx('relative flex shrink-0 items-center justify-center', className)}
      style={{
        width: `${displayWidth}px`,
        height: `${displayHeight}px`,
      }}
    >
      <div
        className='relative flex items-center justify-center'
        style={{
          width: `${BASE_CIRCLE_SIZE}px`,
          height: `${BASE_CIRCLE_SIZE}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
        }}
      >
        {enableLeftWaves && <AvatarWaves side='left' />}

        <div
          className={clsx(
            'relative z-10 flex items-center justify-center overflow-hidden',
            'rounded-full',
            showImage ? (iconMode ? 'bg-brand-750' : 'bg-gradient-30') : 'bg-gray-340',
            'border-3 border-brand-300'
          )}
          style={{
            width: '51.37px',
            height: '51.37px',
            boxSizing: 'border-box',
          }}
        >
          {videoSrc ? (
            <video
              ref={videoRef}
              src={videoSrc}
              className='size-full object-cover'
              loop={videoPlaybackMode === 'loop'}
              muted
              autoPlay
              playsInline
            />
          ) : showImage ? (
            <img
              src={finalSrc}
              alt={alt}
              className={iconMode ? 'object-contain' : 'size-full object-cover'}
              loading='lazy'
              onError={() => setImgError(true)}
            />
          ) : (
            <span
              className={clsx(
                'font-proxima font-bold',
                'text-20 leading-20 tracking-normal',
                'uppercase',
                'text-brand-800'
              )}
            >
              {initials || alt.slice(0, 2)}
            </span>
          )}
        </div>

        <AvatarWaves side='right' />
      </div>
    </div>
  );
};
